import { useEffect } from 'react'

// ----------------------------------------------------------------------

/**
 * Warn the user before they lose an in-progress upload.
 *
 * While `isUploading` is true, a `beforeunload` listener is attached so a
 * browser refresh / tab-close / window-close shows the native "leave site?"
 * prompt. The listener is removed the moment `isUploading` flips false (success,
 * failure, or abort), so there is no sticky guard. Note: browsers only show a
 * GENERIC prompt — a custom message cannot be displayed (spec since ~2017).
 *
 * In-app (react-router) navigation is intentionally NOT guarded here with
 * `useBlocker`: this app mounts a declarative `<BrowserRouter>` (see main.tsx),
 * not a data router (`createBrowserRouter` + `RouterProvider`). `useBlocker`
 * calls `useDataRouterContext`, which throws an `invariant` when no
 * `DataRouterContext` is present — verified against react-router's source — so
 * calling it under `<BrowserRouter>` would crash on every mount. Converting the
 * app to a data router is a global routing change out of scope here. In-app
 * sidebar/link navigation is instead covered by the modal Dialog overlay while
 * the upload is locked (the overlay makes the rest of the page non-interactive);
 * the only residual gap is the browser Back button.
 */
export function useUploadGuard(isUploading: boolean): void {
  useEffect(() => {
    if (!isUploading) return undefined

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Legacy `returnValue` assignment kept for older browsers (Chrome <119 etc.);
      // the prompt text is always the browser's generic message regardless of value.
      // Accessed via a cast so the deprecated-property hint doesn't fire — the
      // assignment is still required for the prompt to show in those browsers.
      ;(e as unknown as { returnValue: string }).returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isUploading])
}
